import { useEffect, useState, useCallback } from 'react';
import intl from 'react-intl-universal';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import { selectIsUserLd, selectUserType } from 'state/User/userSlice';
import {
  getUserSettingValues,
  getViewSettingValues,
  selectCurrentViewSettingValue,
} from 'state/ViewSettings/viewSettingsSlice';
import {
  getLearningTeams,
  selectLearningTeamStatus,
  selectLearningTeamIsFetched,
} from 'state/LearningTeams/learningTeamsSlice';
import { getAllUsers } from 'state/UsersManagement/usersManagementSlice';
import { selectAllProjectTemplates } from 'state/ProjectTemplates/projectTemplatesSlice';
import MoreActionsPopup from 'Organisms/TableHeaderActionButtons/MoreActionsPopup';
import {
  PROJECTS_LIST_VIEW_MODE,
  PATHS,
  SLICE_STATUS,
  USER_TYPES,
} from 'utils/constants';
import CreateProjectModal from './components/CreateProject/CreateProjectModal';
import BoardView from './views/BoardView';
import ViewModeToggleButton from './components/ViewModeToggleButton';
import ImportProjectModal from './components/ImportProjectModal/ImportProjectModal';
import ConsolidatedProjectsView from './views/ConsolidatedProjectsView/ConsolidatedProjectsView';

const ProjectsListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLDUser = useSelector(selectIsUserLd);
  const userType = useSelector(selectUserType);
  const isFacilitatorUser = userType === USER_TYPES.FACILITATOR;
  const projectTemplates = useSelector(selectAllProjectTemplates);
  const viewType = useSelector(
    selectCurrentViewSettingValue('PROJECT_VIEW_TYPE')
  );

  const [displayAddProjectModal, setDisplayAddProjectModal] =
    useState<boolean>(false);
  const [displayImportProjectModal, setDisplayImportProjectModal] =
    useState<boolean>(false);

  const init = useCallback(async () => {
    return await Promise.all([
      dispatch(getAllUsers()),
      dispatch(getUserSettingValues()),
      dispatch(getViewSettingValues(['PROJECT_VIEW_TYPE'])),
    ]);
  }, [dispatch]);

  useEffect(() => {
    init();
  }, [init]);

  const ldTeamStatus = useSelector(selectLearningTeamStatus);
  const ldTeamsFetched = useSelector(selectLearningTeamIsFetched);

  useEffect(() => {
    if (!ldTeamsFetched && ldTeamStatus !== SLICE_STATUS.LOADING) {
      dispatch(getLearningTeams());
    }
  }, [ldTeamStatus, ldTeamsFetched, dispatch]);

  const handleAddProject = () => {
    if (projectTemplates.length === 1) {
      return history.push(
        `${PATHS.NEW_PROJECT_PAGE}/${projectTemplates[0].id}`
      );
    }
    setDisplayAddProjectModal(true);
  };

  return (
    <div className='flex flex-col h-full'>
      {!isFacilitatorUser && (
        <CreateProjectModal
          showModal={displayAddProjectModal}
          onCloseModal={() => setDisplayAddProjectModal(false)}
        />
      )}
      {!isFacilitatorUser && (
        <ImportProjectModal
          showModal={displayImportProjectModal}
          onClose={() => setDisplayImportProjectModal(false)}
        />
      )}

      <PageTitle
        titleComponent={intl.get('PROJECTS_LIST_PAGE.TITLE')}
        headerChildren={
          <div className='flex gap-x-4 items-center'>
            <ViewModeToggleButton viewType={viewType} />
            {isLDUser && (
              <MoreActionsPopup
                options={[
                  {
                    label: intl.get('IMPORT_PROJECTS.IMPORT'),
                    selectOption: () => setDisplayImportProjectModal(true),
                    dataCy: 'table-header__import-button',
                  },
                ]}
                hasSplitButton
                splitButtonOnClick={handleAddProject}
                buttonText={intl.get('PROJECTS_LIST_PAGE.ADD_PROJECT_BUTTON')}
                buttonTestId='add-request-button'
                listClassName='w-38.4'
              />
            )}
          </div>
        }
      />
      <div className='relative w-full h-full'>
        {viewType?.value === PROJECTS_LIST_VIEW_MODE.TABLE && (
          <ConsolidatedProjectsView
            handleAddProject={() => setDisplayAddProjectModal(true)}
          />
        )}
        {viewType?.value === PROJECTS_LIST_VIEW_MODE.BOARD && (
          <BoardView handleAddProject={() => setDisplayAddProjectModal(true)} />
        )}
      </div>
    </div>
  );
};

export default ProjectsListPage;
